import { EditOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  notification,
} from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useState } from 'react';
import LojaService from '../sdk/services/Loja.service';

type ModalRecuperarNotaProps = {
  cnpjEmissor: string;
  serie: any
  tpAmb: string;
  quandoFinalizar?: () => void;
};

type FormularioRecuperacao = {
  dataInicial: moment.Moment;
  dataFinal: moment.Moment;
};

const ModalRecuperarNota = ({
  cnpjEmissor,
  serie,
  tpAmb,
  quandoFinalizar,
}: ModalRecuperarNotaProps) => {
  const [form] = Form.useForm<FormularioRecuperacao>();

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const abrirModal = useCallback(() => {
    const hoje = moment();

    form.setFieldsValue({
      dataInicial: hoje,
      dataFinal: hoje,
    });

    setShowModal(true);
  }, [form]);

  const fecharModal = useCallback(() => {
    if (loading) return;

    setShowModal(false);
    form.resetFields();
  }, [form, loading]);

  const recuperarNotas = async () => {
    try {
      const valores = await form.validateFields();

      const dataInicial = valores.dataInicial
        .clone()
        .startOf('day')
        .format('YYYY-MM-DD');

      const dataFinal = valores.dataFinal
        .clone()
        .endOf('day')
        .format('YYYY-MM-DD');


      if (valores.dataInicial.isAfter(valores.dataFinal, 'day')) {
        notification.warning({
          message: 'Período inválido',
          description:
            'A data inicial não pode ser maior que a data final.',
          top: 80,
        });

        return;
      }
      console.log({
        cnpjEmissor,
        serie,
        dataInicial,
        dataFinal,
        tpAmb,
      });
      setLoading(true);

      const resposta = await LojaService.getRecuperarNotas(cnpjEmissor, serie?.serie, dataInicial, dataFinal, tpAmb);

      notification.success({
        message: 'Recuperação concluída',
        description:
          resposta,
        top: 80,
      });

      setShowModal(false);
      form.resetFields();
      quandoFinalizar?.();
    } catch (error: any) {
      /*
       * Quando o validateFields falha, o Ant Design retorna
       * um objeto contendo errorFields.
       */
      if (error?.errorFields) {
        return;
      }

      console.error('Erro ao recuperar notas:', error);

      const mensagemErro =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        'Não foi possível recuperar as notas do período informado.';

      notification.error({
        message: 'Erro ao recuperar notas',
        description: mensagemErro,
        top: 80,
      });
    } finally {
      setLoading(false);
    }
  };

   const atualizarNotas = async () => {
    try {
      const valores = await form.validateFields();

      const dataInicial = valores.dataInicial
        .clone()
        .startOf('day')
        .format('YYYY-MM-DD');

      const dataFinal = valores.dataFinal
        .clone()
        .endOf('day')
        .format('YYYY-MM-DD');


      if (valores.dataInicial.isAfter(valores.dataFinal, 'day')) {
        notification.warning({
          message: 'Período inválido',
          description:
            'A data inicial não pode ser maior que a data final.',
          top: 80,
        });

        return;
      }
      console.log({
        cnpjEmissor,
        serie,
        dataInicial,
        dataFinal,
        tpAmb,
      });
      setLoading(true);

      const resposta = await LojaService.getAtualizarNotas(cnpjEmissor, serie?.serie, dataInicial, dataFinal, tpAmb);

      notification.success({
        message: 'Recuperação concluída',
        description:
          resposta,
        top: 80,
      });

      setShowModal(false);
      form.resetFields();
      quandoFinalizar?.();
    } catch (error: any) {
      /*
       * Quando o validateFields falha, o Ant Design retorna
       * um objeto contendo errorFields.
       */
      if (error?.errorFields) {
        return;
      }

      console.error('Erro ao recuperar notas:', error);

      const mensagemErro =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        'Não foi possível recuperar as notas do período informado.';

      notification.error({
        message: 'Erro ao recuperar notas',
        description: mensagemErro,
        top: 80,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Row align="middle" gutter={24}>
        <Col xs={24} lg={12}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={abrirModal}
          >
            Recuperar notas
          </Button>
        </Col>
      </Row>

      <Modal
        title="Recuperar notas"
        visible={showModal}
        onCancel={fecharModal}
        footer={[
          <Button
            key="cancelar"
            onClick={fecharModal}
            disabled={loading}
          >
            Cancelar
          </Button>,

          <Button
            key="recuperar"
            type="primary"
            loading={loading}
            onClick={recuperarNotas}
          >
            Recuperar Notas
          </Button>,

          <Button
            key="recuperar"
            type="primary"
            loading={loading}
            onClick={atualizarNotas}
          >
            Atualizar Notas
          </Button>,
        ]}
        destroyOnClose
        maskClosable={!loading}
        closable={!loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            dataInicial: moment(),
            dataFinal: moment(),
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Data inicial"
                name="dataInicial"
                rules={[
                  {
                    required: true,
                    message: 'Informe a data inicial.',
                  },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabled={loading}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Data final"
                name="dataFinal"
                rules={[
                  {
                    required: true,
                    message: 'Informe a data final.',
                  },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ModalRecuperarNota;